export const TIME_RANGES = [
  { key: '24h',    label: '24h',          ms: 24 * 60 * 60 * 1000 },
  { key: '3d',     label: '3d',           ms: 3 * 24 * 60 * 60 * 1000 },
  { key: '1w',     label: '1w',           ms: 7 * 24 * 60 * 60 * 1000 },
  { key: '2w',     label: '2w',           ms: 14 * 24 * 60 * 60 * 1000 },
  { key: '1m',     label: '1m',           ms: 30 * 24 * 60 * 60 * 1000 },
  { key: 'launch', label: 'Since Launch', ms: null },
];

export const MET_CHOICES = [
  // Buoy sensors
  'wind_speed', 'wind_direction_true',
  // External station sensors
  'wind_speed_ext', 'wind_direction_ext',
  'precip_accum_1hr_ext', 'precip_accum_24hr_ext', 'air_temperature_ext',
  // Gridded RTMA/HRRR/MRMS sensors
  'wind_speed_2m_grid', 'wind_direction_grid', 'wind_speed_grid',
  'air_temperature_grid', 'air_pressure_grid',
  'solar_radiation_grid', 'par_grid',
  'precip_rate_grid', 'precip_accum_1hr_grid', 'precip_accum_15min_grid',
];

export const GRID_MET_KINDS = [
  'air_temperature_grid',
  'wind_speed_grid',
  'wind_direction_grid',
  'wind_speed_2m_grid',
  'air_pressure_grid',
  'solar_radiation_grid',
  'par_grid',
  'precip_rate_grid',
  'precip_accum_1hr_grid',
  'precip_accum_15min_grid',
];

export const ROSE_TICKS = {
  vals: [0, 45, 90, 135, 180, 225, 270, 315],
  text: ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'],
};

// Per-sensor depth palette for charts that split a reading into surface
// (depth 1) and bottom (depth 2) traces. Lives here rather than inside a single
// chart component so the water quality charts and the HQ buoys page paint the
// same sensor the same way.
export const DEPTH_COLORS = {
  pc:                    { d1: '#16a34a', d1fill: 'rgba(22,163,74,0.15)',    d2: '#1e3a5f', d2fill: 'rgba(30,58,95,0.15)' },
  pc_fluorometer:        { d1: '#16a34a', d1fill: 'rgba(22,163,74,0.15)',    d2: '#1e3a5f', d2fill: 'rgba(30,58,95,0.15)' },
  chla:                  { d1: '#2e7d32', d1fill: 'rgba(46,125,50,0.15)',   d2: '#ff9800', d2fill: 'rgba(255,152,0,0.15)' },
  chla_fluorometer:      { d1: '#2e7d32', d1fill: 'rgba(46,125,50,0.15)',   d2: '#ff9800', d2fill: 'rgba(255,152,0,0.15)' },
  turbidity_fluorometer: { d1: '#d32f2f', d1fill: 'rgba(211,47,47,0.15)',   d2: '#1976d2', d2fill: 'rgba(25,118,210,0.15)' },
  temperature:           { d1: '#d32f2f', d1fill: 'rgba(211,47,47,0.15)',   d2: '#1976d2', d2fill: 'rgba(25,118,210,0.15)' },
  temperature_rdo:       { d1: '#d32f2f', d1fill: 'rgba(211,47,47,0.15)',   d2: '#1976d2', d2fill: 'rgba(25,118,210,0.15)' },
  temperature_rdo_corrected: { d1: '#d32f2f', d1fill: 'rgba(211,47,47,0.15)', d2: '#1976d2', d2fill: 'rgba(25,118,210,0.15)' },
  o2_25:                 { d1: '#6a1b9a', d1fill: 'rgba(106,27,154,0.15)',  d2: '#26a69a', d2fill: 'rgba(38,166,154,0.15)' },
  o2_rdo:                { d1: '#6a1b9a', d1fill: 'rgba(106,27,154,0.15)',  d2: '#26a69a', d2fill: 'rgba(38,166,154,0.15)' },
  o2_rdo_saturation:     { d1: '#5e35b1', d1fill: 'rgba(94,53,177,0.15)',   d2: '#43a047', d2fill: 'rgba(67,160,71,0.15)' },
  conductivity:          { d1: '#5e35b1', d1fill: 'rgba(94,53,177,0.15)',   d2: '#43a047', d2fill: 'rgba(67,160,71,0.15)' },
};

export const FALLBACK_DEPTH_COLORS = { d1: '#2d6e8e', d1fill: 'rgba(45,110,142,0.18)', d2: '#e0664a', d2fill: 'rgba(224,102,74,0.15)' };

// A buoy can carry a second, comparison instrument for the same parameter — the
// Turner fluorometers reporting as `pc`/`chla`, the legacy "25" probes on
// temperature/O2. When both are present the comparison sensor is drawn over the
// primary one in its own hue pair, dotted and unfilled, so it reads as an
// overlay rather than competing with the primary sensor's filled areas.
//
// Both traces are dotted, so hue is the only thing separating this pair's
// surface from its bottom — they have to diverge hard. The previous amber/
// magenta pair did not (ΔE 14.4 for normal vision, 9.1 simulated colorblind),
// and read as one color at line width. Amber deepens to keep the comparison
// sensor's established identity; bottom moves to violet. The pair now clears
// ΔE 32.8/31.4, and every cross-pair against DEPTH_COLORS' surface green and
// bottom navy clears the ≥15 normal and ≥8 colorblind floors as well.
export const COMPARISON_DEPTH_COLORS = { d1: '#854d0e', d2: '#7c3aed' };

export const SENSOR_TYPES = {
  pc:                    { name: 'Phycocyanin T',             unit: 'μg/L',  color: '#1976d2' },
  pc_fluorometer:        { name: 'Phycocyanin',           unit: 'μg/L',  color: '#1976d2' },
  chla:                  { name: 'Chlorophyll-a T',           unit: 'μg/L',  color: '#388e3c' },
  chla_fluorometer:      { name: 'Chlorophyll-a',         unit: 'μg/L',  color: '#388e3c' },
  turbidity_fluorometer: { name: 'Turbidity',               unit: 'NTU',   color: '#d32f2f' },
  temperature:           { name: 'Temperature 25',             unit: '°F',    color: '#f57c00' },
  temperature_rdo:       { name: 'Temperature',         unit: '°F',    color: '#f57c00' },
  temperature_rdo_corrected: { name: 'Temperature',     unit: '°F',    color: '#f57c00' },
  o2_25:                 { name: 'Dissolved Oxygen 25',        unit: 'mg/L',  color: '#7b1fa2' },
  o2_rdo:                { name: 'Dissolved Oxygen',    unit: 'mg/L',  color: '#7b1fa2' },
  o2_rdo_saturation:     { name: 'O2 Saturation',       unit: '%',     color: '#7b1fa2' },
  conductivity:          { name: 'Conductivity',            unit: 'uS/cm', color: '#7b1fa2' },
  cyanobacteria_index:   { name: 'Cyano Risk Index',        unit: '',      color: '#b71c1c' },
  wave_height:           { name: 'Wave Height',             unit: 'cm',    color: '#8e24aa' },
  wave_direction:        { name: 'Wave Direction',          unit: '°',     color: '#fb8c00' },
  wave_direction_true:   { name: 'Wave Direction (True)',   unit: '°',     color: '#ef6c00' },
  wind_speed:            { name: 'Wind Speed',              unit: 'm/s',   color: '#1e88e5' },
  wind_direction_true:   { name: 'Wind Direction (True)',   unit: '°',     color: '#43a047' },
  wind_speed_ext:        { name: 'Wind Speed (Ext)',        unit: 'm/s',   color: '#1565c0' },
  wind_direction_ext:    { name: 'Wind Direction (Ext)',    unit: '°',     color: '#2e7d32' },
  precip_accum_1hr_ext:  { name: 'Precip Accum 1h (Ext)',  unit: 'mm',    color: '#546e7a' },
  precip_accum_24hr_ext: { name: 'Precip Accum 24h (Ext)', unit: 'mm',    color: '#263238' },
  air_temperature_ext:      { name: 'Air Temperature (Ext)',      unit: '°C',          color: '#c6362e' },
  // Gridded RTMA/HRRR/MRMS
  air_temperature_grid:     { name: 'Air Temp (Grid)',            unit: '°C',          color: '#e53935' },
  wind_speed_grid:          { name: 'Wind Speed 10m (Grid)',      unit: 'm/s',         color: '#1976d2' },
  wind_direction_grid:      { name: 'Wind Dir (Grid)',            unit: '°',           color: '#388e3c' },
  wind_speed_2m_grid:       { name: 'Wind Speed 2m (Grid)',       unit: 'm/s',         color: '#0288d1' },
  air_pressure_grid:        { name: 'Air Pressure (Grid)',        unit: 'hPa',         color: '#7b1fa2' },
  solar_radiation_grid:     { name: 'Solar Radiation (Grid)',     unit: 'W/m²',        color: '#f57f17' },
  par_grid:                 { name: 'PAR (Grid)',                 unit: 'µmol/m²/s',   color: '#ff8f00' },
  precip_rate_grid:         { name: 'Precip Rate (Grid)',         unit: 'mm/hr',       color: '#0097a7' },
  precip_accum_1hr_grid:    { name: 'Precip 1hr (Grid)',          unit: 'mm',          color: '#00796b' },
  precip_accum_15min_grid:  { name: 'Precip 15min (Grid)',        unit: 'mm',          color: '#004d40' },
  // Derived client-side from precip_accum_1hr_grid (summed per Eastern calendar
  // day); not fetched from the API. See MetChart's daily-bar handling.
  precip_accum_24hr_grid:   { name: 'Precip 24h (Grid)',          unit: 'mm',          color: '#00695c' },
};

export const CALM_WIND_THRESHOLD_MS = 0.5;

// Date-axis tick labels: once ticks are spaced less than a day apart, a
// date-only label repeats across several ticks — add the time of day (in
// 12-hour am/pm form) so each tick is distinguishable. All chart timestamps
// are displayed in US/Eastern (see the note on the Water Quality Data tab).
export const DATE_AXIS_TICK_FORMAT_STOPS = [
  { dtickrange: [null, 86400000], value: '%b %d<br>%I:%M %p' },
  { dtickrange: [86400000, null], value: '%b %d' },
];

const EASTERN_TZ = 'America/New_York';

const easternPartsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: EASTERN_TZ,
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit',
  hour12: false,
});

// Plotly's date axis reads a Date object's LOCAL wall-clock fields (then re-encodes
// them as UTC-equivalent ms for plotting/display) — so charts render in whichever
// timezone the *viewer's browser* is set to, not necessarily US/Eastern. To make
// every chart display Eastern regardless of the viewer's own timezone, convert each
// timestamp to its Eastern wall-clock fields and build the Date via the local
// constructor — Plotly then displays exactly those Eastern field values everywhere
// (axis ticks, hover, range bounds), as long as every timestamp goes through this
// same conversion before reaching Plotly.
export const toEasternPlotDate = (input) => {
  const instant = input instanceof Date ? input : new Date(input);
  if (isNaN(instant)) return instant;
  const parts = {};
  for (const { type, value } of easternPartsFormatter.formatToParts(instant)) {
    if (type !== 'literal') parts[type] = value;
  }
  return new Date(
    Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    parts.hour === '24' ? 0 : Number(parts.hour),
    Number(parts.minute), Number(parts.second)
  );
};

export const WATER_QUALITY_SENSOR_ORDER = [
  'pc', 'pc_fluorometer', 'chla', 'chla_fluorometer',
  'temperature', 'temperature_rdo',
  'o2_25', 'o2_rdo', 'o2_rdo_saturation',
  'conductivity', 'turbidity_fluorometer',
];
